import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { preferencesApi } from '../api/preferencesApi';
import { useAuth } from './AuthContext';
import { formatAmount, formatDate, formatDateTime, formatShortDate } from '../lib/currencyUtils';

// ── Country → currencies map (mirrors SupportedCurrency enum on the backend) ──
export const COUNTRY_CURRENCY_MAP = {
  'Kenya':                  [{ code: 'KES', symbol: 'KSh' }],
  'United States':          [{ code: 'USD', symbol: '$'   }],
  'United Kingdom':         [{ code: 'GBP', symbol: '£'   }],
  'Eurozone':               [{ code: 'EUR', symbol: '€'   }],
  'India':                  [{ code: 'INR', symbol: '₹'   }],
  'South Africa':           [{ code: 'ZAR', symbol: 'R'   }],
  'Singapore':              [{ code: 'SGD', symbol: 'S$'  }],
  'United Arab Emirates':   [{ code: 'AED', symbol: 'د.إ' }],
  'Nigeria':                [{ code: 'NGN', symbol: '₦'   }],
  'Uganda':                 [{ code: 'UGX', symbol: 'USh' }],
  'Tanzania':               [{ code: 'TZS', symbol: 'TSh' }],
  'Ghana':                  [{ code: 'GHS', symbol: 'GH₵' }],
  'Ethiopia':               [{ code: 'ETB', symbol: 'Br'  }],
};

export const COUNTRY_REGION_DEFAULTS = {
  'Kenya':                { timezone: 'Africa/Nairobi', locale: 'en-KE' },
  'United States':        { timezone: 'America/New_York', locale: 'en-US' },
  'United Kingdom':       { timezone: 'Europe/London', locale: 'en-GB' },
  'Eurozone':             { timezone: 'Europe/Berlin', locale: 'en-DE' },
  'India':                { timezone: 'Asia/Kolkata', locale: 'en-IN' },
  'South Africa':         { timezone: 'Africa/Johannesburg', locale: 'en-ZA' },
  'Singapore':            { timezone: 'Asia/Singapore', locale: 'en-SG' },
  'United Arab Emirates': { timezone: 'Asia/Dubai', locale: 'en-AE' },
  'Nigeria':              { timezone: 'Africa/Lagos', locale: 'en-NG' },
  'Uganda':               { timezone: 'Africa/Kampala', locale: 'en-UG' },
  'Tanzania':             { timezone: 'Africa/Dar_es_Salaam', locale: 'en-TZ' },
  'Ghana':                { timezone: 'Africa/Accra', locale: 'en-GH' },
  'Ethiopia':             { timezone: 'Africa/Addis_Ababa', locale: 'en-ET' },
};

const DEFAULT = {
  country: 'Kenya',
  currency: 'KES',
  symbol: 'KSh',
  timezone: 'Africa/Nairobi',
  locale: 'en-KE',
};
const STORAGE_KEY = 'currencyPreference';

function symbolFor(code) {
  for (const list of Object.values(COUNTRY_CURRENCY_MAP)) {
    const match = list.find(c => c.code === code);
    if (match) return match.symbol;
  }
  return code;
}

function regionFor(country) {
  return COUNTRY_REGION_DEFAULTS[country] || COUNTRY_REGION_DEFAULTS.Kenya;
}

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persist(pref) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pref)); } catch {}
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [pref, setPref] = useState(() => {
    const stored = loadStored();
    if (!stored) return DEFAULT;
    const region = regionFor(stored.country);
    return {
      ...DEFAULT,
      ...stored,
      timezone: stored.timezone || region.timezone,
      locale: stored.locale || region.locale,
      symbol: stored.symbol || symbolFor(stored.currency || DEFAULT.currency),
    };
  });

  const setLocalPreference = useCallback((country, currency, extras = {}) => {
    const region = regionFor(country);
    const next = {
      country,
      currency,
      symbol: symbolFor(currency),
      timezone: extras.timezone || region.timezone,
      locale: extras.locale || region.locale,
    };
    setPref(next);
    persist(next);
  }, []);

  useEffect(() => {
    preferencesApi.getSystemDefault().then(res => {
      const { country, currency, timezone, locale, symbol } = res.data || {};
      const region = regionFor(country || DEFAULT.country);
      const next = {
        country: country || DEFAULT.country,
        currency: currency || DEFAULT.currency,
        symbol: symbol || symbolFor(currency || DEFAULT.currency),
        timezone: timezone || region.timezone,
        locale: locale || region.locale,
      };
      setPref(next);
      persist(next);
    }).catch(() => {});
  }, [user]);

  const fmtCtx = useMemo(
    () => ({ locale: pref.locale, timezone: pref.timezone }),
    [pref.locale, pref.timezone],
  );

  const value = useMemo(() => ({
    currency: pref.currency,
    country: pref.country,
    symbol: pref.symbol,
    timezone: pref.timezone,
    locale: pref.locale,
    setLocalPreference,
    formatMoney: (amount, code) => formatAmount(amount, code || pref.currency, { locale: pref.locale }),
    formatDate: (value, options) => formatDate(value, fmtCtx, options),
    formatDateTime: (value) => formatDateTime(value, fmtCtx),
    formatShortDate: (value) => formatShortDate(value, fmtCtx),
  }), [pref, setLocalPreference, fmtCtx]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
